import React, { useRef, useState } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, useTheme, styled, CircularProgress } from '@mui/material';
import { MdUploadFile } from 'react-icons/md';
import Input from 'components/Common/Input';
import { Control, Controller } from 'react-hook-form';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

interface CustomizeTelephonySectionProps {
  control: Control<any>;
  logoUrl?: string;
  onLogoUpload: (file: File) => Promise<void>;
  isUploadingLogo?: boolean;
}

export default function CustomizeTelephonySection({
  control,
  logoUrl,
  onLogoUpload,
  isUploadingLogo = false
}: CustomizeTelephonySectionProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleBoxClick = () => {
    if (!isUploadingLogo) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await onLogoUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await onLogoUpload(file);
    }
  };

  return (
    <>
      {/* SEPARATE CARD CONTAINER FOR LOGO & FRIENDLY NAME */}
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          padding: { xs: 2, lg: 3.5 },
          borderRadius: '8px',
          border: '1px solid #ECECEC',
          mt: 3,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Typography variant='subtitle1' fontWeight={500} fontSize='16px' color='common.black' mb={0.5}>
          Customize Your Telephony Service
        </Typography>
        <Typography variant='body2' color='secondary.dark' fontSize='14px' mb={3}>
          White-label this integration with your own logo and display name so customers see your brand instead of the
          default branding.
        </Typography>

        <Typography variant='body2' fontWeight={600} fontSize='15px' color='secondary.dark' mb={1.2}>
          Logo
        </Typography>

        <Box
          onClick={handleBoxClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '1px dashed #E2E8F0',
            borderRadius: '8px',
            py: 5,
            px: 3,
            textAlign: 'center',
            cursor: isUploadingLogo ? 'not-allowed' : 'pointer',
            backgroundColor: isDragging ? '#F8FAFC' : '#FFFFFF',
            transition: 'all 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '140px',
            mb: 3.5,
            position: 'relative',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'primary.light'
            }
          }}
        >
          <VisuallyHiddenInput
            type='file'
            ref={fileInputRef}
            onChange={handleFileChange}
            accept='image/png, image/svg+xml, image/jpeg'
          />

          {isUploadingLogo ? (
            <CircularProgress size={32} thickness={4} sx={{ color: 'primary.main' }} />
          ) : logoUrl ? (
            <Box display='flex' flexDirection='column' alignItems='center' gap={1.5}>
              <Box
                component='img'
                src={logoUrl}
                alt='Custom Logo'
                sx={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain' }}
              />
              <Typography variant='caption' fontWeight={500} color='primary.main' sx={{ textDecoration: 'underline' }}>
                Click to replace logo
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MdUploadFile style={{ color: theme.palette.primary.main, fontSize: '30px' }} />
              </Box>
              <Typography variant='body1' fontWeight={500} color='common.black' fontSize='14px'>
                Click to upload or drag and drop
              </Typography>
              <Typography variant='body2' color='secondary.dark' fontSize='12px' sx={{ mt: 0.5 }}>
                Recommended size: 200×60px, PNG or SVG format.
              </Typography>
            </>
          )}
        </Box>

        <Input
          name='friendly_name'
          control={control}
          fullWidth
          label='Display Name'
          required
          rules={{ required: 'Display Name is required' }}
          placeholder='Smart Connections'
        />
      </Box>

      {/* CHECKBOX OUTSIDE ALL CONTAINERS */}
      <Box mt={3} mb={1} sx={{ display: 'flex', alignItems: 'center' }}>
        <Controller
          name='active'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  color='primary'
                />
              }
              label={
                <Typography variant='body2' color='#1E293B' fontWeight={500} fontSize='14px'>
                  By enabling this, you confirm that all telephony credentials and configurations have been completed
                  correctly.
                </Typography>
              }
            />
          )}
        />
      </Box>
    </>
  );
}
